import { useState, useEffect, useRef, useCallback, forwardRef, useImperativeHandle } from "react";
import { MonitorIcon, Loader2Icon } from "lucide-react";

import Message from "../../../../i18n/Message";

import "./PreviewContent.scss";

const AUTO_REFRESH_INTERVAL = 10000; // 10 seconds

const PreviewContent = forwardRef(({
  previewUrl,
  currentPath,
  onPathChange,
  isAgentRunning,
  agentSession,
}, ref) => {
  const iframeRef = useRef(null);
  const [isLoading, setIsLoading] = useState(false);
  const [hasUserInteracted, setHasUserInteracted] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const prevIsAgentRunningRef = useRef(isAgentRunning);
  const prevPreviewUrlRef = useRef(previewUrl);
  const agentCompletedRef = useRef(false);

  // Full URL for iframe
  const fullUrl = previewUrl ? `${previewUrl}${currentPath}` : null;

  // Expose refresh method to parent via ref
  useImperativeHandle(ref, () => ({
    refresh: () => {
      setRefreshKey((k) => k + 1);
      setIsLoading(true);
    },
  }), []);

  // Auto-refresh while agent is running (starts when plan is approved)
  useEffect(() => {
    if (!isAgentRunning || hasUserInteracted || !previewUrl) return;

    const interval = setInterval(() => {
      setRefreshKey((k) => k + 1);
    }, AUTO_REFRESH_INTERVAL);

    return () => clearInterval(interval);
  }, [isAgentRunning, hasUserInteracted, previewUrl]);

  // Track when agent completes (isAgentRunning transitions from true to false)
  useEffect(() => {
    const wasRunning = prevIsAgentRunningRef.current;
    const nowRunning = isAgentRunning;
    const currentStatus = agentSession?.status;

    console.log("[PreviewContent] Agent state check:", {
      wasRunning,
      nowRunning,
      currentStatus,
      previewUrl: !!previewUrl,
    });

    // Mark agent as completed when it stops running
    if (wasRunning && !nowRunning && currentStatus === "COMPLETED") {
      console.log("[PreviewContent] Agent completed - marking for refresh");
      agentCompletedRef.current = true;

      // If previewUrl is available, refresh immediately
      if (previewUrl) {
        console.log("[PreviewContent] Preview URL available - triggering refresh");
        setRefreshKey((k) => k + 1);
        agentCompletedRef.current = false;
      }
    }

    prevIsAgentRunningRef.current = nowRunning;
  }, [isAgentRunning, agentSession?.status, previewUrl]);

  // Refresh when previewUrl changes (including subdomain changes)
  useEffect(() => {
    const prevUrl = prevPreviewUrlRef.current;
    const currentUrl = previewUrl;

    console.log("[PreviewContent] previewUrl effect:", { prevUrl, currentUrl });

    // If previewUrl just became available and agent had completed, trigger refresh
    if (!prevUrl && currentUrl && agentCompletedRef.current) {
      console.log("[PreviewContent] Preview URL now available after agent completed - triggering refresh");
      setRefreshKey((k) => k + 1);
      agentCompletedRef.current = false;
    }
    // If previewUrl changed (e.g., subdomain update), trigger refresh
    else if (prevUrl && currentUrl && prevUrl !== currentUrl) {
      console.log("[PreviewContent] Preview URL changed - triggering refresh");
      setRefreshKey((k) => k + 1);
    }

    prevPreviewUrlRef.current = currentUrl;
  }, [previewUrl]);

  // Handle user interaction with iframe
  const handleIframeMouseDown = useCallback(() => {
    setHasUserInteracted(true);
  }, []);

  // Handle iframe load
  const handleIframeLoad = useCallback(() => {
    setIsLoading(false);
    // Try to get current path from iframe (may fail due to CORS)
    try {
      const iframe = iframeRef.current;
      if (iframe?.contentWindow?.location) {
        const iframePath = iframe.contentWindow.location.pathname;
        if (iframePath && iframePath !== currentPath) {
          onPathChange(iframePath);
        }
      }
    } catch (e) {
      // CORS restriction - can't access iframe location
    }
  }, [currentPath, onPathChange]);

  // No preview URL available - show placeholder
  if (!previewUrl) {
    return (
      <div className="preview_content">
        <div className="placeholder">
          <MonitorIcon size={48} color="#d1d5db" />
          <p className="placeholder_text">
            <Message
              id="builder_preview_placeholder"
              defaultMessage="Preview will appear here"
            />
          </p>
        </div>
      </div>
    );
  }

  console.log("[PreviewContent] Rendering iframe:", { fullUrl, refreshKey });

  return (
    <div className="preview_content preview_content--active">
      {isLoading && (
        <div className="loading_overlay">
          <Loader2Icon size={24} className="spinning" />
        </div>
      )}
      <iframe
        ref={iframeRef}
        key={refreshKey}
        src={fullUrl}
        className="preview_iframe"
        onLoad={handleIframeLoad}
        onMouseDown={handleIframeMouseDown}
        title="Project Preview"
      />
    </div>
  );
});

PreviewContent.displayName = "PreviewContent";

export default PreviewContent;
